import React, { FC } from 'react'
import { Product as ProductType, ShopifyStorefrontProduct } from '@/types'
import { GetStaticProps, GetStaticPaths } from 'next'
import { client } from '@/lib/client'
import { ProductView } from '@/views/ProductView'
import { PageProps } from '@/types/shared'
import { shopifyClient } from '@/lib/client/shopify'
import { gql } from 'graphql-request'
import { NotFound } from '@/views/NotFound'
import { Sentry } from '@/lib/sentry'
import { Paginated, unwindEdges } from '@good-idea/unwind-edges'

interface ProductPageProps extends PageProps {
  product?: ProductType
}

const Product: FC<ProductPageProps> = ({ product }) => {
  if (!product || !product.shopify) return <NotFound />
  return <ProductView product={product} />
}

/**
 * Initial Props
 */

export const getStaticProps: GetStaticProps<ProductPageProps> = async (ctx) => {
  try {
    const { params } = ctx
    if (!params?.handle || typeof params.handle !== 'string') {
      return { notFound: true }
    }

    const [product, siteSettings] = await Promise.all([
      client.getProduct(params.handle),
      client.fetchSiteSettings(),
    ])
    if (!product) {
      return { notFound: true, props: { siteSettings } }
    }
    return { props: { product, siteSettings }, revalidate: 10 }
  } catch (e) {
    Sentry.captureException(e, 'page_fetch_error', ctx)
    return { notFound: true, props: { foo: 'bar' }, revalidate: 1 }
  }
}

/**
 * Static Paths
 */

const productHandlesQuery = gql`
  query ProductHandlesQuery {
    products(first: 1000) {
      edges {
        cursor
        node {
          id
          handle
        }
      }
    }
  }
`

type HandlesResponse = {
  products: Paginated<ShopifyStorefrontProduct>
}

export const getStaticPaths: GetStaticPaths = async () => {
  try {
    const result =
      await shopifyClient.query<HandlesResponse>(productHandlesQuery)
    const [products] = unwindEdges(result?.products)
    const paths = products.map((product) => ({
      params: {
        handle: product.handle,
      },
    }))
    return { paths, fallback: 'blocking' }
  } catch (e) {
    Sentry.captureException(e, 'paths_fetch_error', {
      route: '/products/[handle]',
    })
    return { paths: [], fallback: 'blocking' }
  }
}
export default Product
